/* ══════════════════════════════════════════════════════════════
   App.ts — Root App Component
   Renders navigation + page content shell
   ══════════════════════════════════════════════════════════════ */

import { renderNavigation, initThemeToggle } from './components/Navigation';
import { PAGE_SHELL_STYLES } from './components/PageShell';
import { MARKET_STATUS_BAR_STYLES } from './components/MarketStatusBar';
import { initSearchModal } from './components/SearchModal';
import { renderTerminalShell, initTerminalShell, TERMINAL_SHELL_STYLES } from './components/terminal/TerminalShell';
import { initChatWidget, CHAT_WIDGET_STYLES } from './components/ChatWidget';
import { registerPage, initRouter } from './router/router';
import { getThemePreference } from './router/guards';
import { isFeatureEnabled } from './services/runtime-config';
import { initMarketingSite } from './marketing/site';
import {
  renderDashboard, initDashboard,
  renderSessionRadar, initSessionRadar,
  renderStrategy, initStrategy,
  renderIntelligence, initIntelligence,
  renderMarkets, initMarkets,
  renderIPOEarnings, initIPOEarnings,
  renderBacktest, initBacktest,
  renderSettings, initSettings,
  renderPortfolio, initPortfolio,
  renderWorldMonitor, initWorldMonitor,
  renderOptions, initOptions,
  renderChat, initChat, CHAT_PAGE_STYLES,
} from './pages';

const API_WARNING_STYLES = `
  .api-warning-banner {
    position: fixed;
    left: 16px;
    right: 16px;
    bottom: 16px;
    z-index: 300;
    border: 1px solid rgba(255, 122, 26, 0.45);
    border-radius: 10px;
    background: rgba(12, 18, 28, 0.94);
    color: #f3efe7;
    padding: 10px 12px;
    display: grid;
    gap: 6px;
    box-shadow: 0 10px 28px rgba(0, 0, 0, 0.35);
    font-family: 'IBM Plex Mono', monospace;
  }

  .api-warning-banner strong {
    color: #ff7a1a;
    font-size: 12px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .api-warning-banner p {
    margin: 0;
    color: rgba(243, 239, 231, 0.9);
    font-size: 12px;
    line-height: 1.45;
  }

  .api-warning-banner button {
    justify-self: start;
    border: 1px solid rgba(255, 122, 26, 0.45);
    background: rgba(255, 122, 26, 0.14);
    color: #f3efe7;
    border-radius: 6px;
    padding: 6px 10px;
    font: inherit;
    cursor: pointer;
  }
`;

function renderApiWarningBanner(message: string): void {
  if (document.getElementById('api-warning-banner')) return;

  const banner = document.createElement('section');
  banner.id = 'api-warning-banner';
  banner.className = 'api-warning-banner';
  banner.setAttribute('role', 'alert');
  banner.innerHTML = `
    <strong>Data Feed Warning</strong>
    <p>${message}</p>
    <button type="button" id="api-warning-dismiss">Dismiss</button>
  `;

  document.body.appendChild(banner);
  const dismiss = document.getElementById('api-warning-dismiss');
  dismiss?.addEventListener('click', () => banner.remove());
}

async function checkApiHealthInProduction(): Promise<void> {
  if (import.meta.env.DEV) return;

  try {
    const response = await fetch('/api/version', { method: 'GET' });
    if (response.ok) return;

    if (response.status === 503) {
      const payload = await response.json().catch(() => null) as { message?: string } | null;
      const message = payload?.message || 'API service unavailable. Verify Cloudflare Pages variable API_PROXY_ORIGIN.';
      renderApiWarningBanner(message);
      return;
    }

    renderApiWarningBanner(`API health check failed with status ${response.status}. Data-driven charts may be unavailable.`);
  } catch {
    renderApiWarningBanner('Unable to reach /api/version. Check Cloudflare deploy and API proxy settings.');
  }
}

/**
 * Initialize the application
 */
export function initApp(): void {
  if (isFeatureEnabled('marketing_site_v1')) {
    initMarketingSite();
    return;
  }

  // Set initial theme
  document.documentElement.setAttribute('data-theme', getThemePreference());

  // Inject component styles
  injectStyles();

  // Register all page renderers with their init functions
  registerPage('/', renderDashboard, initDashboard);
  registerPage('/session-radar', renderSessionRadar, initSessionRadar);
  registerPage('/strategy', renderStrategy, initStrategy);
  registerPage('/intelligence', renderIntelligence, initIntelligence);
  registerPage('/markets', renderMarkets, initMarkets);
  registerPage('/options', renderOptions, initOptions);
  registerPage('/ipo-earnings', renderIPOEarnings, initIPOEarnings);
  registerPage('/backtest', renderBacktest, initBacktest);
  registerPage('/settings', renderSettings, initSettings);
  registerPage('/portfolio', renderPortfolio, initPortfolio);
  registerPage('/world-monitor', renderWorldMonitor, initWorldMonitor);
  registerPage('/chat', renderChat, initChat);

  // Render app shell
  const app = document.getElementById('app');
  if (!app) return;

  const terminalV2Enabled = isFeatureEnabled('terminal_ui_v2');

  app.innerHTML = terminalV2Enabled
    ? renderTerminalShell('<div id="page-content" class="page-enter"></div>')
    : `
      ${renderNavigation()}
      <div class="app-content">
        <div id="page-content" class="page-enter"></div>
      </div>
    `;

  // Initialize router (handles initial page render)
  initRouter();

  if (terminalV2Enabled) {
    initTerminalShell();
  } else {
    // Initialize theme toggle
    initThemeToggle();
  }

  if (isFeatureEnabled('chat_ui_v1')) {
    initChatWidget();
  }

  // Initialize search modal (⌘K)
  initSearchModal();

  // Surface Cloudflare API misconfiguration early so charts don't fail silently.
  void checkApiHealthInProduction();
}

/**
 * Inject component-level styles into <head>
 */
function injectStyles(): void {
  const style = document.createElement('style');
  style.textContent = `${PAGE_SHELL_STYLES}\n${MARKET_STATUS_BAR_STYLES}\n${TERMINAL_SHELL_STYLES}\n${CHAT_PAGE_STYLES}\n${CHAT_WIDGET_STYLES}\n${API_WARNING_STYLES}`;
  document.head.appendChild(style);
}
